/**
 * V50 — Payment Screenshot Upload form.
 *
 * Rendered on /pay-now AFTER the parent has accepted the 9 Parent Terms
 * (via PaymentConsentGate) and the QR code / UPI details are visible.
 *
 * Posts multipart/form-data to /api/payment-submissions. On success shows a
 * confirmation card with the short reference the admin team can match
 * against the WhatsApp / email follow-up.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2, FileImage, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 10;
const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";

export default function PaymentScreenshotUpload({ prefill }) {
    const [name, setName] = useState(prefill?.name || "");
    const [phone, setPhone] = useState(prefill?.phone || "");
    const [amount, setAmount] = useState("");
    const [reference, setReference] = useState(prefill?.reference_short || "");
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);

    const handleFile = (f) => {
        if (!f) return;
        if (!ACCEPT.split(",").includes(f.type)) {
            toast.error("Only JPG, PNG or WebP images are accepted.");
            return;
        }
        if (f.size > MAX_MB * 1024 * 1024) {
            toast.error(`File is too large. Max ${MAX_MB} MB.`);
            return;
        }
        setFile(f);
    };

    const clearFile = () => setFile(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || name.trim().length < 2) {
            toast.error("Please enter your name.");
            return;
        }
        if (!phone.trim() || phone.replace(/\D/g, "").length < 7) {
            toast.error("Please enter a valid phone number.");
            return;
        }
        if (!file) {
            toast.error("Please attach your payment screenshot.");
            return;
        }
        setSubmitting(true);
        setProgress(0);
        try {
            const fd = new FormData();
            fd.append("name", name.trim());
            fd.append("phone", phone.trim());
            if (amount) fd.append("amount", amount.trim());
            if (reference) fd.append("reference", reference.trim());
            if (notes) fd.append("notes", notes.trim());
            fd.append("file", file);
            const res = await axios.post(`${API}/payment-submissions`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
                },
            });
            setResult(res.data);
            toast.success("Payment screenshot received — thank you!");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Upload failed. Please try again or send via WhatsApp.";
            toast.error(typeof detail === "string" ? detail : "Upload failed.");
        } finally {
            setSubmitting(false);
        }
    };

    if (result) {
        return (
            <div
                data-testid="payment-submission-success"
                className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-6 sm:p-8"
            >
                <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-xl font-bold text-[#0F172A] mb-1">
                            Payment received — thank you!
                        </h3>
                        <p className="text-sm text-emerald-900 mb-3">{result.message}</p>
                        <div className="inline-flex items-center gap-2 text-xs bg-white px-3 py-2 rounded-lg border border-emerald-200">
                            <span className="text-slate-500">Submission Reference:</span>
                            <code className="font-mono font-bold text-[#1E3A8A]">{result.reference_short}</code>
                        </div>
                        <p className="text-xs text-slate-500 mt-3">
                            Please keep this reference for your records. Our team will verify and confirm via WhatsApp / email within one working day.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="payment-submission-form"
            className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-7 shadow-sm space-y-4"
            noValidate
        >
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Parent Name *" testid="pay-input-name">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        autoComplete="name"
                        className={inputCls}
                        data-testid="pay-input-name"
                    />
                </Field>
                <Field label="Phone / WhatsApp *" testid="pay-input-phone">
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        autoComplete="tel"
                        placeholder="+91 9xxxx xxxxx"
                        className={inputCls}
                        data-testid="pay-input-phone"
                    />
                </Field>
                <Field label="Amount Paid (₹)">
                    <input
                        type="text"
                        inputMode="numeric"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                        placeholder="e.g. 5000"
                        className={inputCls}
                        data-testid="pay-input-amount"
                    />
                </Field>
                <Field label="Reference (optional)">
                    <input
                        type="text"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        placeholder="From consent or UPI"
                        className={inputCls}
                        data-testid="pay-input-reference"
                    />
                </Field>
            </div>

            <Field label="Notes (optional)">
                <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything we should know — e.g. child's name, board, monthly plan."
                    className={`${inputCls} resize-y`}
                    data-testid="pay-input-notes"
                />
            </Field>

            {/* File picker */}
            <Field label="Payment Screenshot * (JPG / PNG / WebP, max 10 MB)">
                {file ? (
                    <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-emerald-300 bg-emerald-50">
                        <FileImage className="w-5 h-5 text-emerald-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[#0F172A] truncate">{file.name}</div>
                            <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB · {file.type}</div>
                        </div>
                        <button
                            type="button"
                            onClick={clearFile}
                            className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700"
                            aria-label="Remove file"
                            data-testid="pay-clear-file"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <label className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-4 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-[#1E3A8A] transition-colors cursor-pointer">
                        <Upload className="w-6 h-6 text-[#1E3A8A] shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[#0F172A]">
                                Tap to choose screenshot or take a photo
                            </div>
                            <div className="text-xs text-slate-500">
                                JPG, PNG or WebP · up to 10 MB · we auto-compress large images
                            </div>
                        </div>
                        <input
                            type="file"
                            accept={ACCEPT}
                            onChange={(e) => handleFile(e.target.files?.[0])}
                            className="sr-only"
                            data-testid="pay-input-file"
                        />
                    </label>
                )}
            </Field>

            {submitting && progress > 0 && progress < 100 && (
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#1E3A8A] transition-all" style={{ width: `${progress}%` }} />
                </div>
            )}

            <button
                type="submit"
                disabled={submitting}
                data-testid="pay-submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1E3A8A] hover:bg-[#1E40AF] disabled:bg-slate-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
                {submitting ? (
                    <>
                        <Loader2 className="w-5 h-5 animate-spin" /> Uploading {progress}%
                    </>
                ) : (
                    <>
                        <Upload className="w-5 h-5" /> Submit Payment Details
                    </>
                )}
            </button>
        </form>
    );
}

const inputCls =
    "w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]";

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5 block">
            {label}
        </span>
        {children}
    </label>
);
